import { getProviderRequiredEnvVars } from './providerCatalog';

/**
 * Defines patterns to detect required environment variables in strategy code.
 * Each object needs:
 * - pattern: A RegExp to match in the code.
 * - key: The corresponding environment variable key name.
 * - description: A short description for UI hints (optional).
 * - docsUrl: A direct link to the API key generation/management page (optional).
 */
export const REQUIRED_ENV_VAR_PATTERNS = [
  {
    // Matches specific PolygonDataBacktesting class usage or imports
    pattern: /PolygonDataBacktesting|from\s+polygon\s+import|import\s+polygon|polygon\s+api|POLYGON_API_KEY/i,
    key: "POLYGON_API_KEY",
    description: "Required for Polygon.io market data during backtesting.",
    docsUrl: "https://polygon.io/dashboard/", // Direct link to API keys
    allowedLengths: [32], // Added exact length
    contexts: ['backtesting'], // Required only for backtesting
    providers: ['polygon'],
  },
  {
    // Matches the use of PerplexityHelper
    pattern: /PerplexityHelper|PERPLEXITY_API_KEY/i,
    key: "PERPLEXITY_API_KEY",
    description: "Required for Perplexity AI integration.",
    docsUrl: "https://www.perplexity.ai/settings/api", // Updated link to API settings
    allowedLengths: [53], // Updated length to match example key
    // No 'contexts' means required in all contexts where detection occurs
  },
  {
    // Matches the use of GrokHelper
    pattern: /GrokHelper|GROK_API_KEY/i,
    key: "GROK_API_KEY",
    description: "Required for Grok AI integration.",
    docsUrl: "https://console.x.ai/", // Link to main product page (no direct public API key page)
    allowedLengths: [84], // Updated length
  },
  {
    // Matches the use of QuiverHelper
    pattern: /QuiverHelper|QUIVER_API_KEY/i,
    key: "QUIVER_API_KEY",
    description: "Required for Quiver Quantitative data.",
    docsUrl: "https://api.quiverquant.com/", // Link to account page
  },
  {
    // Matches the use of XaiHelper
    pattern: /XaiHelper|XAI_API_KEY/i,
    key: "XAI_API_KEY",
    description: "Required for xAI integration.",
    docsUrl: "https://console.x.ai/", // Link to main xAI page (no direct public API key page)
    allowedLengths: [84], // Updated length
  },
  {
    pattern: /TradierHelper|TRADIER_ACCESS_TOKEN/i,
    key: "TRADIER_ACCESS_TOKEN",
    description: "Tradier Access Token for API usage.",
    docsUrl: "https://www.tradier.com/",
    allowedLengths: [28],
  },
  {
    pattern: /TradierHelper|TRADIER_ACCOUNT_NUMBER/i,
    key: "TRADIER_ACCOUNT_NUMBER",
    description: "Tradier Account Number (ID).",
    docsUrl: "https://www.tradier.com/",
    // No allowedLengths - account numbers can be 8 or 10 characters
  },
  {
    pattern: /AlpacaHelper|ALPACA_API_KEY/i,
    key: "ALPACA_API_KEY",
    description: "Alpaca API Key.",
    docsUrl: "https://alpaca.markets/",
    allowedLengths: [20, 26], // Support legacy 20-char keys and newer 26-char keys
    providers: ['alpaca'],
  },
  {
    pattern: /AlpacaHelper|ALPACA_API_SECRET/i,
    key: "ALPACA_API_SECRET",
    description: "Alpaca API Secret.",
    docsUrl: "https://alpaca.markets/",
    allowedLengths: [40, 44], // Support legacy 40-char secrets and newer 44-char secrets
    providers: ['alpaca'],
    // No 'contexts' means required in all contexts where detection occurs
  },
  {
    // Matches DataBentoDataBacktesting usage or imports
    pattern: /DataBentoDataBacktesting|from\s+lumibot\.backtesting\s+import.*DataBentoDataBacktesting|DATABENTO_API_KEY/i,
    key: "DATABENTO_API_KEY",
    description: "Required for DataBento market data during futures backtesting.",
    docsUrl: "https://databento.com/", // DataBento main portal
    allowedLengths: [32], // Example: db-B5bvkH6BLmDUGWpCnyTfnAyxEW8nw
    contexts: ['backtesting'], // Required only for backtesting with DataBento
    providers: ['databento'], // Only required when DataBento is the selected data provider
  },
  {
    // Matches Tradovate broker usage
    pattern: /TRADOVATE_USERNAME|Tradovate|TradovateStrategy/i,
    key: "TRADOVATE_USERNAME",
    description: "Required for Tradovate broker authentication.",
    docsUrl: "https://tradovate.com/",
    contexts: ['deployment'], // Required only for deployment
  },
  {
    // Matches Tradovate broker usage
    pattern: /TRADOVATE_PASSWORD|Tradovate|TradovateStrategy/i,
    key: "TRADOVATE_PASSWORD",
    description: "Required for Tradovate broker authentication.",
    docsUrl: "https://tradovate.com/",
    contexts: ['deployment'], // Required only for deployment
  },
  {
    // Matches Tradovate broker usage
    pattern: /TRADOVATE_CID|Tradovate|TradovateStrategy/i,
    key: "TRADOVATE_CID",
    description: "Required for Tradovate broker authentication.",
    docsUrl: "https://tradovate.com/",
    contexts: ['deployment'], // Required only for deployment
  },
  {
    // Matches Tradovate broker usage
    pattern: /TRADOVATE_SECRET|Tradovate|TradovateStrategy/i,
    key: "TRADOVATE_SECRET",
    description: "Required for Tradovate broker authentication.",
    docsUrl: "https://tradovate.com/",
    contexts: ['deployment'], // Required only for deployment
  },
  // Note: Project X brokers (TopstepX, Top One Futures, etc.) use broker-specific environment variables
  // like PROJECTX_TOPSTEPX_API_KEY, PROJECTX_TOPONE_API_KEY, etc.
  // These are handled automatically by the broker configuration forms and don't need 
  // generic patterns that could cause validation conflicts.
  // Add more patterns here as needed
];

/**
 * Analyzes strategy code to detect required environment variables for a given context.
 * @param {string} code The strategy code to analyze.
 * @param {string} [context] Optional context (e.g., 'backtesting', 'deployment').
 * @returns {Array<string>} An array of required environment variable key names.
 */
export const detectRequiredEnvVars = (code, context, provider) => {
  const normalizedProvider = typeof provider === 'string' ? provider.trim().toLowerCase() : undefined;

  // Custom provider means user brings their own credentials - nothing is required
  if (normalizedProvider === 'custom') {
    return [];
  }

  const requiredKeys = new Set(getProviderRequiredEnvVars(normalizedProvider));

  if (!code) {
    return Array.from(requiredKeys);
  }
  const allPatterns = Array.isArray(REQUIRED_ENV_VAR_PATTERNS) ? REQUIRED_ENV_VAR_PATTERNS : [];

  // Broker config variables that are automatically handled by our broker config forms
  // These should NOT appear as environment variables that users need to set manually
  const BROKER_CONFIG_VARIABLES = [
    'TRADOVATE_USERNAME',
    'TRADOVATE_PASSWORD',
    'TRADOVATE_CID',
    'TRADOVATE_SECRET',
    'ALPACA_API_KEY',
    'ALPACA_API_SECRET',
    'TRADIER_ACCESS_TOKEN',
    'TRADIER_ACCOUNT_NUMBER',
    // Project X broker-specific variables are handled by broker forms:
    // PROJECTX_TOPSTEPX_API_KEY, PROJECTX_TOPONE_API_KEY, etc.
    // These don't need generic patterns since they're broker-specific
  ];

  allPatterns.forEach(({ pattern, key, contexts: patternContexts, providers: patternProviders }) => {
    // If a context is provided, the pattern must either not specify contexts (applies to all)
    // or include the current context.
    // If no context is provided, assume all patterns are potentially relevant (e.g. generic analysis).
    const contextMatch = !context || !patternContexts || patternContexts.includes(context);
    // Only match patterns that are either not provider-scoped or explicitly include this provider
    const providerMatch =
      !normalizedProvider ||
      !patternProviders ||
      patternProviders.includes(normalizedProvider);

    // Exclude broker config variables when in deployment context - these are handled by broker forms
    const isBrokerConfig = BROKER_CONFIG_VARIABLES.includes(key);
    const shouldExcludeBrokerConfig = context === 'deployment' && isBrokerConfig;

    if (contextMatch && providerMatch && pattern.test(code) && !shouldExcludeBrokerConfig) {
      requiredKeys.add(key);
    }
  });

  return Array.from(requiredKeys);
};

/**
 * Returns a human-readable description of the expected length(s) for an environment variable.
 */
export const describeExpectedLength = (patternDetails) => {
  if (!patternDetails) return null;

  if (Array.isArray(patternDetails.allowedLengths) && patternDetails.allowedLengths.length > 0) {
    const sorted = [...patternDetails.allowedLengths].sort((a, b) => a - b);
    if (sorted.length === 1) {
      return `${sorted[0]} characters`;
    }
    if (sorted.length === 2) {
      return `${sorted[0]} or ${sorted[1]} characters`;
    }
    const allButLast = sorted.slice(0, -1).join(', ');
    const last = sorted[sorted.length - 1];
    return `${allButLast}, or ${last} characters`;
  }

  return null;
};

/**
 * Validates that an environment variable value matches the expected length (if any).
 */
export const isValueLengthValid = (patternDetails, value) => {
  if (!patternDetails) return true;
  if (value == null) return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  const length = trimmed.length;

  if (Array.isArray(patternDetails.allowedLengths) && patternDetails.allowedLengths.length > 0) {
    return patternDetails.allowedLengths.includes(length);
  }

  return true;
};

/**
 * Checks the status of required environment variables against the provided custom variables.
 * @param {Array<string>} requiredKeys Array of required env var keys (e.g., ["POLYGON_API_KEY"]).
 * @param {Array<{key: string, value: string}>} customEnvVars Array of user-provided env vars.
 * @returns {{
 *   required: Array<string>,
 *   missing: Array<string>, // Keys that are not present OR present but with invalid length
 *   present: Array<string>, // Keys that are present AND have valid length (if specified)
 *   invalidLengthDetails: Array<{key: string, expectedLength: Array<number> | undefined, actualLength: number, message: string}>
 * }} Status object.
 */
export const checkRequiredEnvVarsStatus = (requiredKeys, customEnvVars) => {
  const present = [];
  const missing = [];
  const invalidLengthDetails = [];
  const patterns = Array.isArray(REQUIRED_ENV_VAR_PATTERNS) ? REQUIRED_ENV_VAR_PATTERNS : [];

  requiredKeys.forEach(reqKey => {
    const envVar = customEnvVars.find(ev => ev.key === reqKey);
    const patternDetails = patterns.find(p => p.key === reqKey);

    if (envVar?.savedSecretReference) {
      present.push(reqKey);
    } else if (envVar && envVar.value && envVar.value.trim() !== "") {
      if (patternDetails && !isValueLengthValid(patternDetails, envVar.value)) {
        const trimmedLength = envVar.value.trim().length;
        const detail = {
          key: reqKey,
          expectedLength: patternDetails.allowedLengths,
          actualLength: trimmedLength,
          message: (() => {
            const expectedDescription = describeExpectedLength(patternDetails);
            return expectedDescription
              ? `${reqKey} has an incorrect length. Expected ${expectedDescription}, got ${trimmedLength}.`
              : `${reqKey} has an incorrect length.`;
          })(),
        };
        invalidLengthDetails.push(detail);
        missing.push(reqKey);
      } else {
        present.push(reqKey);
      }
    } else {
      // Variable is not present or has no value
      missing.push(reqKey);
    }
  });

  return {
    required: requiredKeys,
    missing: missing, // Contains keys not found OR found with invalid length
    present: present, // Contains keys found AND with valid length
    invalidLengthDetails: invalidLengthDetails,
  };
};
